import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/ppcp";
import { setUserId, track } from "@/lib/analytics";

const AuthContext = createContext(null);

/** Session state. PPCP holds no user record — this mirrors what WordPress told us. */
export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: "loading", user: null, entitlements: [] });
  // Analytics fires once per app load, never again on an entitlement refresh.
  const opened = useRef(false);
  // One event per distinct reason: the transient anonymous state during a magic-link exchange
  // must not silence the no-entitlement report that follows it.
  const deniedReasons = useRef(new Set());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({
        status: "authenticated",
        user: data.user,
        entitlements: data.entitlements ?? [],
        upgrades: data.upgrades ?? {},
        upgradeUrl: data.upgrade_url,
        // Operator-only chrome (content preview) is gated on this, never on the URL flag.
        canPreview: !!data.can_preview,
        // Set while WordPress is unreachable: existing access keeps working, nothing new unlocks.
        entitlementsFromCache: !!data.entitlements_from_cache,
      });
      return true;
    } catch {
      setState({ status: "anonymous", user: null, entitlements: [] });
      return false;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const report = (reason) => {
      if (deniedReasons.current.has(reason)) return;
      deniedReasons.current.add(reason);
      track("access_denied", { reason, page_path: window.location.pathname });
    };

    if (state.status === "authenticated" && !opened.current) {
      opened.current = true;
      if (state.user?.wp_user_id) setUserId(state.user.wp_user_id);
      track("app_opened", {
        plans_owned: state.entitlements?.length ?? 0,
        entitlements_from_cache: !!state.entitlementsFromCache,
      });
    }
    // Signed in but owning nothing is a blocked reader too — reported once.
    if (state.status === "authenticated" && !state.entitlements?.length) {
      report("no_entitlement");
    }
    if (state.status === "anonymous") {
      report("no_session");
    }
  }, [state]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setState({ status: "anonymous", user: null, entitlements: [] });
    }
  }, []);

  /** Force an immediate re-read of entitlements from WordPress. */
  const refreshEntitlements = useCallback(async () => {
    try {
      const { data } = await api.post("/entitlements/refresh");
      setState((prev) => ({ ...prev, entitlements: data.entitlements ?? [] }));
    } catch {
      /* falls back to the cached set */
    }
  }, []);

  // Returning to the tab after a purchase or upgrade elsewhere re-reads entitlements,
  // so a new program appears without waiting for the cache to expire.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && state.status === "authenticated") {
        refreshEntitlements();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [state.status, refreshEntitlements]);

  const value = useMemo(
    () => ({
      ...state,
      refresh,
      refreshEntitlements,
      logout,
      tierFor: (slug) => state.entitlements.find((e) => e.program_slug === slug)?.tier ?? null,
    }),
    [state, refresh, refreshEntitlements, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
